import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {AdminSetupEmail, AdminSignup, AdminSuppression} from '../../domain/admin';
import {EmailStatusBadge, SignupStatusBadge, SuppressionBadge} from './AdminBadges';
import {Field, FieldList, PaddleLink, Panel, Timestamp} from './AdminPrimitives';
import Modal from '../workspace/Modal';
import {primaryButtonClass, secondaryButtonClass, Spinner} from '../formStyles';
import {AlertTriangleIcon, MailIcon} from '../Icons';

/**
 * The setup email — the only thing in this panel that writes anything.
 *
 * For an account provisioned by a Paddle checkout this email *is* the account: they have no
 * password, so the magic link is the only door. Resending it is what an operator is on this page to
 * do, which is why it sits above the activity tabs rather than in a menu.
 *
 * Four wording rules, all of them easy to get wrong and invisible when you do:
 *
 *  - **`sent` is not `delivered`.** The badge says "Accepted by provider" and gets no green check.
 *  - **`suppressed` is the trap.** Resend accepts the send, returns a message id, and drops it. The
 *    send *looks* successful. So an active suppression is stated next to the button rather than
 *    left in a timeline, and the button itself changes rather than staying inviting.
 *  - **No record ≠ never sent.** A null summary means this account predates the email logging.
 *  - **Statuses that sit at `sent` are not broken.** Delivery only advances once the Resend webhook
 *    is registered in an environment, so a timeline that never reaches "Delivered" is expected.
 */

interface AdminSetupEmailCardProps {
  email: string;
  passwordSet: boolean;
  setupEmail: AdminSetupEmail | null;
  signup: AdminSignup | null;
  /** Non-null ⇒ every send to this address is being dropped. */
  suppression: AdminSuppression | null;
  /**
   * Resolves with whether the server refused for a suppression. The dialog closes on every other
   * outcome and stays open on that one, turning itself into the override — so it has to be told,
   * rather than inferring it from a prop that lands mid-await.
   */
  onResend: (options: {force: boolean}) => Promise<{suppressed: boolean}>;
  isResending: boolean;
  resendError: string | null;
  /** From a 409 — the server refused because the address is suppressed. */
  suppressedRefusal: {suppression: AdminSuppression; remedy: string} | null;
  /** Set after a send that went out over a known suppression. Warn, do not congratulate. */
  forcedPastSuppression: boolean;
}

const AdminSetupEmailCard: React.FC<AdminSetupEmailCardProps> = ({
  email,
  passwordSet,
  setupEmail,
  signup,
  suppression,
  onResend,
  isResending,
  resendError,
  suppressedRefusal,
  forcedPastSuppression,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [forceNext, setForceNext] = useState(false);

  // A suppression known before the click and one discovered by the 409 are the same situation.
  const activeSuppression = suppressedRefusal?.suppression
    ?? (suppression && !suppression.removedAt ? suppression : null);

  // The refusal arrives while the dialog is open; keeping it open turns "Send it" into the
  // override, which is exactly the second click the operator now has to make deliberately.
  useEffect(() => {
    if (suppressedRefusal) {
      setIsConfirmOpen(true);
      setForceNext(true);
    }
  }, [suppressedRefusal]);

  const handleConfirm = async () => {
    const {suppressed} = await onResend({force: forceNext});

    // A refusal keeps the dialog up so the next click is the deliberate override. Anything else —
    // sent, forced, or a genuine failure — is reported on the card behind it.
    if (!suppressed) {
      setIsConfirmOpen(false);
    }
  };

  const openDialog = () => {
    // Never pre-arm the override: force is something you choose after being told why, so a fresh
    // dialog always starts as an ordinary send even when we can see the suppression already.
    setForceNext(false);
    setIsConfirmOpen(true);
  };

  return (
    <Panel
      title="Setup email"
      subtitle="A fresh magic link. For an account with no password, this is the only way in."
      actions={
        <button
          type="button"
          onClick={openDialog}
          disabled={isResending}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium
           text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed
           cursor-pointer ${
    activeSuppression ? 'bg-gray-700 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-500'
    }`}
        >
          {isResending ? <Spinner /> : <MailIcon className="h-3.5 w-3.5" />}
          Resend setup email
        </button>
      }
    >
      {activeSuppression && (
        <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-red-100">
            <AlertTriangleIcon className="h-3.5 w-3.5 shrink-0" />
            Resend is suppressing {activeSuppression.email}
          </p>
          <p className="mt-1 text-[11px] text-red-200/90">
            Sending to this address returns success and delivers nothing. Resending will not reach
            them — clear the suppression in the Resend dashboard first.
          </p>
          {suppressedRefusal?.remedy && (
            <p className="mt-1.5 text-[11px] text-red-100">{suppressedRefusal.remedy}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <SuppressionBadge
              origin={activeSuppression.origin}
              removedAt={activeSuppression.removedAt}
            />
            <span className="text-[11px] text-red-200/70">
              since <Timestamp iso={activeSuppression.suppressedAt} />
            </span>
            <Link
              to={`/admin/emails?tab=suppressions&search=${encodeURIComponent(activeSuppression.email)}`}
              className="text-[11px] text-red-200 underline hover:text-red-100"
            >
              View suppression
            </Link>
          </div>
        </div>
      )}

      {forcedPastSuppression && (
        <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200">
          That send was forced past an active suppression. Resend accepted it, and almost certainly
          dropped it — treat it as not delivered unless the timeline says otherwise.
        </p>
      )}

      {!passwordSet && (
        <p className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200">
          This account has never set a password. Until it does, the emailed link is the only way for
          them to sign in.
        </p>
      )}

      {resendError && !suppressedRefusal && (
        <p className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2 text-[11px] text-red-200">
          {resendError} — the attempt is recorded on the email timeline below either way.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-gray-600">Last setup email</p>
          {!setupEmail ? (
            <p className="py-1 text-xs text-gray-500">
              {/* Not "never sent" — we simply have nothing logged for this account. */}
              No record. Accounts created before email logging existed show nothing here; it does
              not mean none was sent.
            </p>
          ) : (
            <>
              <div className="mb-1.5">
                <EmailStatusBadge status={setupEmail.status} />
              </div>
              <FieldList>
                <Field label="Sent">
                  <Timestamp iso={setupEmail.sentAt} />
                </Field>
                <Field label="Delivered">
                  <Timestamp iso={setupEmail.deliveredAt} fallback="Not confirmed" />
                </Field>
                <Field label="Sent by">{setupEmail.triggeredBy}</Field>
                <Field label="Total sends" hint="How many setup emails this person has had">
                  {setupEmail.sendCount}
                </Field>
                {setupEmail.error && <Field label="Error">{setupEmail.error}</Field>}
              </FieldList>
            </>
          )}
        </div>

        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-gray-600">Signup record</p>
          {!signup ? (
            <p className="py-1 text-xs text-gray-500">
              None — this account predates Paddle-first signup.
            </p>
          ) : (
            <>
              <div className="mb-1.5">
                <SignupStatusBadge status={signup.status} />
              </div>
              <FieldList>
                <Field label="Signup email">{signup.email}</Field>
                <Field label="Started">
                  <Timestamp iso={signup.createdAt} />
                </Field>
                <Field label="Provisioned">
                  <Timestamp iso={signup.provisionedAt} fallback="Not yet" />
                </Field>
                <Field label="Link sent">
                  <Timestamp iso={signup.loginLinkSentAt} fallback="Not yet" />
                </Field>
                <Field label="Expires">
                  <Timestamp iso={signup.expiresAt} />
                </Field>
                <Field label="Paddle customer">
                  <PaddleLink kind="customer" id={signup.paddleCustomerId} />
                </Field>
                <Field label="Paddle subscription">
                  <PaddleLink kind="subscription" id={signup.paddleSubscriptionId} />
                </Field>
              </FieldList>
              {signup.lastError && (
                <p className="mt-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-2
                 text-[11px] text-red-200">
                  {signup.lastError}
                </p>
              )}
              <Link
                to="/admin/signups?status=failed"
                className="mt-2 inline-block text-[11px] text-indigo-400 hover:text-indigo-300"
              >
                Open the signup queue
              </Link>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isConfirmOpen}
        title={forceNext ? 'Send anyway, over the suppression?' : 'Send a new setup email?'}
        subtitle={email}
        onClose={() => setIsConfirmOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isResending}
              className={secondaryButtonClass}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isResending}
              className={primaryButtonClass}
            >
              {isResending && <Spinner />}
              {forceNext ? 'Send anyway' : 'Send it'}
            </button>
          </>
        }
      >
        {forceNext ? (
          <>
            <p className="text-sm text-gray-300">
              {suppressedRefusal?.remedy
                ?? 'This address is on Resend’s suppression list, so the send will be accepted and '
                  + 'then dropped.'}
            </p>
            <p className="mt-2 text-xs text-amber-200">
              Forcing it does not make it arrive. The only thing that does is clearing the
              suppression in the Resend dashboard — send anyway only if you have already done that
              and our mirror is stale.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-300">
              This emails <span className="text-gray-100">{email}</span> a fresh magic link and
              invalidates nothing they already have. It is the same email a new subscriber gets after
              checkout.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Sends are not rate-limited, so this will go out immediately.
            </p>
          </>
        )}
      </Modal>
    </Panel>
  );
};

export default AdminSetupEmailCard;
